/**
 * model.js: the project JSON shape of a detailed HVAC system and of the shared components, with
 * the factories and normalisers every reader goes through (report section 5).
 *
 *   building.hvac_systems[]  detailed entry: { id, kind:'detailed', name, targets, thermostat,
 *                            components:{ zone, system }, zoneOverrides, connections }
 *   building.hvac_doas[]     { id, name, fields }
 *   building.hvac_plant      { chilledWaterLoop|null, hotWaterLoop|null, chillers[], towers[], boilers[] }
 *
 * Template systems keep their schema untouched; `isDetailedSystem` is the only discriminator.
 */
import {
  ZONE_OBJECTS, SYSTEM_OBJECTS, DOAS_OBJECT, LOOP_OBJECTS, EQUIPMENT_OBJECTS, EQUIPMENT_KINDS, LOOP_KINDS,
  storedFields, initialValue, inCatalog, airSystemsForZone, loopNeedsOf, zoneHasDoasField, shortLabel,
} from './catalog.js';

let _seq = 0;
export const uid = (prefix = 'hd') => `${prefix}_${Date.now().toString(36)}_${(_seq++).toString(36)}`;

export const isDetailedSystem = (s) => !!s && s.kind === 'detailed';

/** Every stored field of an object at its initial value. */
export function defaultFields(object) {
  const out = {};
  for (const f of storedFields(object)) out[f.name] = initialValue(object, f.name);
  return out;
}

/** Backfill fields added to the IDD table after a record was stored; unknown keys are kept. */
export function normalizeFields(object, fields) {
  if (!inCatalog(object)) return { ...(fields || {}) };
  const out = { ...defaultFields(object), ...(fields || {}) };
  return out;
}

export function createComponent(object, overrides = {}) {
  if (!inCatalog(object)) throw new Error(`Not in the detailed HVAC catalog: ${object}`);
  const c = { object, fields: { ...defaultFields(object), ...(overrides.fields || {}) } };
  if (ZONE_OBJECTS.includes(object)) c.doasId = overrides.doasId ?? null;
  return c;
}

export const DEFAULT_THERMOSTAT = () => ({
  heating: { mode: 'schedule', scheduleRef: 'code', value: 21 },
  cooling: { mode: 'schedule', scheduleRef: 'code', value: 24 },
});

/**
 * A new detailed system. `zoneObject` may be null (the builder places it); when an air system is
 * required and exactly one is valid, it is placed too.
 */
export function createDetailedSystem({ name = '', zoneObject = null, systemObject = null, targets = [] } = {}) {
  const sys = {
    id: uid('hvacd'), kind: 'detailed', name,
    targets: [...targets],
    thermostat: DEFAULT_THERMOSTAT(),
    components: { zone: null, system: null },
    zoneOverrides: {},
    connections: [],
  };
  if (zoneObject) sys.components.zone = createComponent(zoneObject);
  if (systemObject) sys.components.system = createComponent(systemObject);
  else if (zoneObject) {
    const valid = airSystemsForZone(zoneObject);
    if (valid.length === 1) sys.components.system = createComponent(valid[0]);
  }
  sys.connections = deriveConnections(sys);
  return sys;
}

/** The edges the engine draws, derived from the components and their coil choices (report section 4.4). */
export function deriveConnections(sys, doasList = []) {
  const out = [];
  const z = sys.components?.zone; const s = sys.components?.system;
  if (z && s) {
    const f = Object.keys(z.fields || {}).concat(['Template VRF System Name', 'Template Unitary System Name', 'Template VAV System Name', 'Template Constant Volume System Name']);
    const field = f.find((n) => /^Template .* System Name$/.test(n)) || 'Template System Name';
    out.push({ from: 'zone', to: 'system', field });
  }
  const push = (from, needs) => {
    for (const kind of LOOP_KINDS) for (const n of needs[kind]) out.push({ from, to: `plant:${kind}`, field: n.field });
  };
  if (z) push('zone', loopNeedsOf(z.object, z.fields));
  if (s) push('system', loopNeedsOf(s.object, s.fields));
  const doasIds = new Set();
  if (z?.doasId) doasIds.add(z.doasId);
  for (const o of Object.values(sys.zoneOverrides || {})) if (o?.doasId) doasIds.add(o.doasId);
  for (const id of doasIds) {
    const d = doasList.find((x) => x.id === id);
    out.push({ from: 'zone', to: `doas:${id}`, field: 'Dedicated Outdoor Air System Name' });
    if (d) push(`doas:${id}`, loopNeedsOf(DOAS_OBJECT, d.fields));
  }
  return out;
}

/** Backfill a stored detailed system (new IDD fields, missing keys) without changing its meaning. */
export function withDetailedDefaults(sys) {
  if (!isDetailedSystem(sys)) return sys;
  const comps = sys.components || {};
  const zone = comps.zone ? { ...comps.zone, fields: normalizeFields(comps.zone.object, comps.zone.fields), doasId: comps.zone.doasId ?? null } : null;
  const system = comps.system ? { ...comps.system, fields: normalizeFields(comps.system.object, comps.system.fields) } : null;
  const t = sys.thermostat || {};
  const side = (k, dflt) => ({ mode: t[k]?.mode === 'constant' ? 'constant' : 'schedule', scheduleRef: t[k]?.scheduleRef || 'code', value: Number.isFinite(Number(t[k]?.value)) ? Number(t[k].value) : dflt });
  const out = {
    ...sys,
    targets: Array.isArray(sys.targets) ? sys.targets : [],
    thermostat: { heating: side('heating', 21), cooling: side('cooling', 24) },
    components: { zone, system },
    zoneOverrides: sys.zoneOverrides && typeof sys.zoneOverrides === 'object' ? sys.zoneOverrides : {},
  };
  out.connections = deriveConnections(out);
  return out;
}

// ── Shared components ─────────────────────────────────────────────────────────
export function createDoas(name = 'DOAS') {
  return { id: uid('doas'), name, fields: defaultFields(DOAS_OBJECT) };
}
export function normalizeDoas(d) {
  return { id: d.id || uid('doas'), name: d.name || 'DOAS', fields: normalizeFields(DOAS_OBJECT, d.fields) };
}
export function createLoop(kind) {
  return { fields: defaultFields(LOOP_OBJECTS[kind]) };
}
export function createEquipment(kind, name) {
  const object = EQUIPMENT_OBJECTS[kind];
  return { id: uid(kind.slice(0, -1)), name: name || shortLabel(object), fields: defaultFields(object) };
}
export const blankPlant = () => ({ chilledWaterLoop: null, hotWaterLoop: null, chillers: [], towers: [], boilers: [] });
export function normalizePlant(p) {
  const out = blankPlant();
  if (!p || typeof p !== 'object') return out;
  if (p.chilledWaterLoop) out.chilledWaterLoop = { fields: normalizeFields(LOOP_OBJECTS.chilledWater, p.chilledWaterLoop.fields) };
  if (p.hotWaterLoop) out.hotWaterLoop = { fields: normalizeFields(LOOP_OBJECTS.hotWater, p.hotWaterLoop.fields) };
  for (const k of EQUIPMENT_KINDS) {
    out[k] = (Array.isArray(p[k]) ? p[k] : []).map((e) => ({ id: e.id || uid(k.slice(0, -1)), name: e.name || shortLabel(EQUIPMENT_OBJECTS[k]), fields: normalizeFields(EQUIPMENT_OBJECTS[k], e.fields) }));
  }
  return out;
}
export const loopKey = (kind) => (kind === 'chilledWater' ? 'chilledWaterLoop' : 'hotWaterLoop');

/** Safe readers: older projects have neither collection. */
export const getDoasList = (b) => (Array.isArray(b?.hvac_doas) ? b.hvac_doas.map(normalizeDoas) : []);
export const getPlant = (b) => normalizePlant(b?.hvac_plant);
export const getDetailedSystems = (b) => (b?.hvac_systems || []).filter(isDetailedSystem).map(withDetailedDefaults);

/** The DOAS a zone of this system references (per-zone override first, then the system default). */
export function doasIdForZone(sys, zoneId) {
  const o = sys.zoneOverrides?.[zoneId];
  if (o && 'doasId' in o) return o.doasId || null;
  return sys.components?.zone?.doasId || null;
}
/** The zone object's fields for one zone (system values overlaid with that zone's overrides). */
export function zoneFieldsFor(sys, zoneId) {
  const base = sys.components?.zone?.fields || {};
  const o = sys.zoneOverrides?.[zoneId]?.fields || {};
  return { ...base, ...o };
}
/** The thermostat spec for one zone (per-zone override first). */
export function thermostatFor(sys, zoneId) {
  const o = sys.zoneOverrides?.[zoneId]?.thermostat;
  const t = sys.thermostat || DEFAULT_THERMOSTAT();
  return o ? { heating: { ...t.heating, ...(o.heating || {}) }, cooling: { ...t.cooling, ...(o.cooling || {}) } } : t;
}
/**
 * A reader's summary of a detailed system: label, the cooling COP it exposes, its heating source
 * and the DSR cost family (split, vrf, chiller) the LCA rates use.
 */
export function detailedSummary(sysIn, building) {
  const sys = withDetailedDefaults(sysIn);
  const z = sys.components.zone; const s = sys.components.system;
  const parts = [];
  if (z) parts.push(shortLabel(z.object));
  if (s) parts.push(shortLabel(s.object));
  const doasIds = new Set([z?.doasId, ...Object.values(sys.zoneOverrides || {}).map((o) => o?.doasId)].filter(Boolean));
  if (doasIds.size) parts.push('DOAS');
  const needs = { chilledWater: false, hotWater: false };
  for (const c of [z, s].filter(Boolean)) { const n = loopNeedsOf(c.object, c.fields); if (n.chilledWater.length) needs.chilledWater = true; if (n.hotWater.length) needs.hotWater = true; }
  if (needs.chilledWater) parts.push('CHW');
  if (needs.hotWater) parts.push('HW');
  const copField = (fields) => { for (const [k, v] of Object.entries(fields || {})) if (/Cooling COP$/.test(k) && Number.isFinite(Number(v)) && v !== '') return Number(v); return null; };
  let coolingCop = copField(s?.fields) ?? copField(z?.fields);
  if (coolingCop == null && needs.chilledWater) {
    const ch = getPlant(building)?.chillers?.[0];
    if (ch && Number.isFinite(Number(ch.fields['Nominal COP']))) coolingCop = Number(ch.fields['Nominal COP']);
  }
  const heatTypes = [];
  for (const c of [z, s].filter(Boolean)) for (const [k, v] of Object.entries(c.fields || {})) if (/(Heating Coil Type|Reheat Coil Type|Supplemental Heating Coil Type|Supplemental Heating or Reheat Coil Type)$/.test(k) && v && v !== 'None') heatTypes.push(String(v));
  const heating = heatTypes.length ? [...new Set(heatTypes)].join(' / ') : 'None';
  const family = z?.object === 'HVACTemplate:Zone:VRF' ? 'vrf' : needs.chilledWater ? 'chiller' : 'split';
  return { label: parts.join(' + ') || 'Detailed system', coolingCop, heating, family, needs };
}

export const zoneCanUseDoas = (sys) => !!sys.components?.zone && zoneHasDoasField(sys.components.zone.object);
export { ZONE_OBJECTS, SYSTEM_OBJECTS, DOAS_OBJECT };
